import api from './api';

export interface ReportSummary {
    totalRevenue: number;
    totalOrders: number;
    totalProducts: number;
    averageTicket: number;
    totalCost: number;
    totalProfit: number;
    profitMargin: number;
}

export interface SalesByPeriod {
    date: string;
    totalAmount: number;
    orderCount: number;
    productsSold: number;
}

export interface SalesByProduct {
    productId: number;
    productName: string;
    category: string;
    quantitySold: number;
    totalRevenue: number;
    averagePrice: number;
}

export interface SalesByCategory {
    categoryId: number;
    categoryName: string;
    productCount: number;
    quantitySold: number;
    totalRevenue: number;
    percentage: number;
}

export interface SalesByPaymentMethod {
    paymentMethodId: number;
    paymentMethodName: string;
    transactionCount: number;
    totalAmount: number;
    percentage: number;
}

export interface SalesByUser {
    userId: number;
    username: string;
    role: string;
    totalSales: number;
    totalRevenue: number;
    averageTicket: number;
}

export interface ReportFilters {
    startDate?: string;
    endDate?: string;
    period?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    categoryId?: number;
    userId?: number;
    paymentMethodId?: number;
}

class ReportsService {
    // Get report summary
    async getSummary(startDate?: string, endDate?: string): Promise<ReportSummary> {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await api.get<ReportSummary>(`/reports/summary?${params.toString()}`);
        return response.data;
    }

    // Get sales by period
    async getSalesByPeriod(
        period: string = 'daily',
        startDate?: string,
        endDate?: string
    ): Promise<SalesByPeriod[]> {
        const params = new URLSearchParams();
        params.append('period', period);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await api.get<SalesByPeriod[]>(`/reports/by-period?${params.toString()}`);
        return response.data;
    }

    // Get sales by product
    async getSalesByProduct(
        startDate?: string,
        endDate?: string,
        categoryId?: number
    ): Promise<SalesByProduct[]> {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (categoryId) params.append('categoryId', categoryId.toString());

        const response = await api.get<SalesByProduct[]>(`/reports/by-product?${params.toString()}`);
        return response.data;
    }

    // Get sales by category
    async getSalesByCategory(startDate?: string, endDate?: string): Promise<SalesByCategory[]> {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await api.get<SalesByCategory[]>(`/reports/by-category?${params.toString()}`);
        return response.data;
    }

    // Get sales by payment method
    async getSalesByPaymentMethod(
        startDate?: string,
        endDate?: string
    ): Promise<SalesByPaymentMethod[]> {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await api.get<SalesByPaymentMethod[]>(
            `/reports/by-payment-method?${params.toString()}`
        );
        return response.data;
    }

    // Get sales by user/cashier
    async getSalesByUser(startDate?: string, endDate?: string): Promise<SalesByUser[]> {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await api.get<SalesByUser[]>(`/reports/by-user?${params.toString()}`);
        return response.data;
    }

    // Export methods
    async exportSalesByPeriod(period: string = 'daily', startDate?: string, endDate?: string): Promise<void> {
        const params = new URLSearchParams();
        params.append('period', period);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await api.get(`/reports/export/by-period?${params.toString()}`, {
            responseType: 'blob'
        });

        this.downloadFile(response.data, `Ventas_por_${period}.xlsx`);
    }

    async exportSalesByProduct(startDate?: string, endDate?: string): Promise<void> {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await api.get(`/reports/export/by-product?${params.toString()}`, {
            responseType: 'blob'
        });

        this.downloadFile(response.data, 'Ventas_por_producto.xlsx');
    }

    async exportSalesByCategory(startDate?: string, endDate?: string): Promise<void> {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await api.get(`/reports/export/by-category?${params.toString()}`, {
            responseType: 'blob'
        });

        this.downloadFile(response.data, 'Ventas_por_categoria.xlsx');
    }

    async exportSalesByPaymentMethod(startDate?: string, endDate?: string): Promise<void> {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await api.get(`/reports/export/by-payment-method?${params.toString()}`, {
            responseType: 'blob'
        });

        this.downloadFile(response.data, 'Ventas_por_metodo_pago.xlsx');
    }

    async exportSalesByUser(startDate?: string, endDate?: string): Promise<void> {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await api.get(`/reports/export/by-user?${params.toString()}`, {
            responseType: 'blob'
        });

        this.downloadFile(response.data, 'Ventas_por_usuario.xlsx');
    }

    private downloadFile(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
}

export const reportsService = new ReportsService();

